import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import {
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, TablePagination, Chip, Avatar, IconButton, Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import { getConfigGet, getConfigPost } from "../getConfig";
import ENDPOINTS from "../apiConstants";
import SettingsIcon from "@material-ui/icons/Settings";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  filterContainer: {
    display: "flex", gap: theme.spacing(2), flexWrap: "wrap", alignItems: "flex-end",
    "& > *": { marginRight: theme.spacing(2) },
  },
  filterField: { minWidth: 180 },
  table: { marginTop: theme.spacing(2) },
  tableHeader: {
    backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[200],
    fontWeight: "bold",
  },
  logo: { width: 60, height: 60, objectFit: "contain" },
  statusChip: { fontWeight: "bold" },
  loadingContainer: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
  circuitOpen: { backgroundColor: "#f44336", color: "white" },
  circuitClosed: { backgroundColor: "#4caf50", color: "white" },
  circuitHalfOpen: { backgroundColor: "#ff9800", color: "white" },
  dialogContent: { minWidth: 400, paddingTop: "8px !important" },
  dialogActions: { padding: theme.spacing(2) },
}));

interface Brand {
  id: number;
  external_key: string;
  display_name: string;
  logo_url: string | null;
  provider_name: string;
  status: string;
  discount_rate: number | null;
  is_blacklisted: boolean;
  circuit_state: string | null;
  next_test_at: string | null;
  disabled_reason: string | null;
  order_id: number | null;
  favourite_order_id: number | null;
}

interface Pagination {
  page: number; page_size: number; total: number;
  total_pages: number; has_next_page: boolean; has_previous_page: boolean;
}

const BrandCatalogTilloHealth = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1, page_size: 10, total: 0, total_pages: 0,
    has_next_page: false, has_previous_page: false,
  });

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("display_name");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [circuitStateFilter, setCircuitStateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isBlacklisted, setIsBlacklisted] = useState("");
  const [providerFilter, setProviderFilter] = useState("1");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [newCircuitState, setNewCircuitState] = useState("");
  const [notes, setNotes] = useState("");
  const [nextTestAt, setNextTestAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getDefaultNextTestAt = () => moment().add(10, "minutes").format("YYYY-MM-DDTHH:mm");
  const getMinDateTime = () => moment().format("YYYY-MM-DDTHH:mm");

  useEffect(() => { fetchBrands(); }, [pagination.page, pagination.page_size, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const params: any = { page: pagination.page, pageSize: pagination.page_size, sortBy, sortOrder };
      if (search) params.search = search;
      if (circuitStateFilter) params.circuit_state = circuitStateFilter;
      if (statusFilter) params.status = statusFilter;
      if (isBlacklisted) params.is_blacklisted = isBlacklisted;
      if (providerFilter) params.provider_id = providerFilter;
      const response: any = await axios.get(ENDPOINTS.BRAND_CATALOG, getConfigGet(params));
      if (response.data.success) {
        setBrands(response.data.data);
        const p = response.data.pagination;
        setPagination({
          page: p.page || 1, page_size: p.page_size || p.pageSize || 10,
          total: p.total, total_pages: p.total_pages || p.totalPages || 0,
          has_next_page: p.has_next_page || false, has_previous_page: p.has_previous_page || false,
        });
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to fetch brands", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setPagination({ ...pagination, page: 1 }); fetchBrands(); };
  const handleClearFilters = () => {
    setSearch(""); setSortBy("display_name"); setSortOrder("ASC");
    setCircuitStateFilter(""); setStatusFilter(""); setIsBlacklisted(""); setProviderFilter("1");
    setPagination({ ...pagination, page: 1 });
    setTimeout(() => fetchBrands(), 100);
  };
  const handlePageChange = (_e: unknown, newPage: number) => setPagination({ ...pagination, page: newPage + 1 });
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setPagination({ ...pagination, page_size: parseInt(e.target.value, 10), page: 1 });

  const openCircuitDialog = (brand: Brand) => {
    setSelectedBrand(brand);
    setNewCircuitState(brand.circuit_state || "closed");
    setNotes("");
    setNextTestAt(getDefaultNextTestAt());
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setDialogOpen(false); setSelectedBrand(null);
    setNewCircuitState(""); setNotes(""); setNextTestAt("");
  };

  const handleUpdateCircuitState = async () => {
    if (!selectedBrand || !newCircuitState) { notify("Please select a circuit state", "error"); return; }
    setSubmitting(true);
    try {
      await axios.post(ENDPOINTS.BRAND_CATALOG, {
        action: "health_update", brand_id: selectedBrand.id,
        circuit_state: newCircuitState,
        notes: notes || null,
        next_test_at: nextTestAt ? moment(nextTestAt).utc().toISOString() : null,
      }, { headers: getConfigPost() });
      notify(`Circuit state updated to ${newCircuitState}`, "success");
      handleCloseDialog();
      fetchBrands();
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to update circuit state", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getCircuitStateChip = (state: string | null | undefined) => {
    if (!state) return <Chip label="N/A" size="small" />;
    const chipClass = state === "open" ? classes.circuitOpen : state === "closed" ? classes.circuitClosed : classes.circuitHalfOpen;
    return <Chip label={state.toUpperCase()} size="small" className={chipClass} />;
  };

  const getStatusColor = (s: string) => s === "enabled" ? "primary" : s === "disabled" ? "secondary" : "default";

  return (
    <div className={classes.root}>
      <h2>Brand Catalog — Brands</h2>
      <Paper style={{ padding: 16 }}>
        <div className={classes.filterContainer}>
          <TextField label="Search" variant="outlined" size="small" value={search}
            onChange={(e) => setSearch(e.target.value)} className={classes.filterField}
            placeholder="Search by name or key..." onKeyPress={(e) => e.key === "Enter" && handleSearch()} />
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as string)} label="Sort By">
              <MenuItem value="id">ID</MenuItem>
              <MenuItem value="display_name">Brand Name</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Sort Order</InputLabel>
            <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as "ASC" | "DESC")} label="Sort Order">
              <MenuItem value="ASC">Ascending</MenuItem>
              <MenuItem value="DESC">Descending</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Circuit State</InputLabel>
            <Select value={circuitStateFilter} onChange={(e) => setCircuitStateFilter(e.target.value as string)} label="Circuit State">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="half_open">Half Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as string)} label="Status">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Blacklisted</InputLabel>
            <Select value={isBlacklisted} onChange={(e) => setIsBlacklisted(e.target.value as string)} label="Blacklisted">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Provider</InputLabel>
            <Select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value as string)} label="Provider">
              <MenuItem value="1">Tillo</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={handleSearch} disabled={loading}>Search</Button>
          <Button variant="outlined" onClick={handleClearFilters}>Clear Filters</Button>
        </div>
      </Paper>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress /></div>
      ) : (
        <Paper className={classes.table}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>ID</TableCell>
                <TableCell className={classes.tableHeader}>Logo</TableCell>
                <TableCell className={classes.tableHeader}>External Key</TableCell>
                <TableCell className={classes.tableHeader}>Display Name</TableCell>
                <TableCell className={classes.tableHeader}>Provider</TableCell>
                <TableCell className={classes.tableHeader}>Status</TableCell>
                <TableCell className={classes.tableHeader}>Discount %</TableCell>
                <TableCell className={classes.tableHeader}>Blacklisted</TableCell>
                <TableCell className={classes.tableHeader}>Sort Order</TableCell>
                <TableCell className={classes.tableHeader}>Fav Order</TableCell>
                <TableCell className={classes.tableHeader}>Circuit State</TableCell>
                <TableCell className={classes.tableHeader}>Disabled Reason</TableCell>
                <TableCell className={classes.tableHeader}>Next Test At</TableCell>
                <TableCell className={classes.tableHeader}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brands.map((brand) => (
                <TableRow key={brand.id}>
                  <TableCell>{brand.id}</TableCell>
                  <TableCell><Avatar src={brand.logo_url || ""} alt={brand.display_name} className={classes.logo} variant="square" /></TableCell>
                  <TableCell>{brand.external_key}</TableCell>
                  <TableCell>{brand.display_name}</TableCell>
                  <TableCell>{brand.provider_name}</TableCell>
                  <TableCell><Chip label={brand.status} color={getStatusColor(brand.status) as any} size="small" className={classes.statusChip} /></TableCell>
                  <TableCell>{brand.discount_rate != null ? `${brand.discount_rate}%` : "-"}</TableCell>
                  <TableCell><Chip label={brand.is_blacklisted ? "Yes" : "No"} color={brand.is_blacklisted ? "secondary" : "default"} size="small" /></TableCell>
                  <TableCell>{brand.order_id ?? "—"}</TableCell>
                  <TableCell>{brand.favourite_order_id ?? "—"}</TableCell>
                  <TableCell>{getCircuitStateChip(brand.circuit_state)}</TableCell>
                  <TableCell>{brand.disabled_reason || "N/A"}</TableCell>
                  <TableCell>{brand.next_test_at ? new Date(brand.next_test_at).toLocaleString() : "N/A"}</TableCell>
                  <TableCell>
                    <Tooltip title={brand.status === "enabled" ? "Update Circuit State" : "Only enabled brands can be updated"}>
                      <span>
                        <IconButton size="small" color="primary" onClick={() => openCircuitDialog(brand)} disabled={brand.status !== "enabled"}>
                          <SettingsIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            count={pagination.total} page={pagination.page - 1}
            onChangePage={handlePageChange} rowsPerPage={pagination.page_size}
            onChangeRowsPerPage={handleRowsPerPageChange} rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Update Circuit State</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <p><strong>Brand:</strong> {selectedBrand?.display_name}</p>
          <p><strong>Current State:</strong> {selectedBrand?.circuit_state || "N/A"}</p>
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel>New Circuit State</InputLabel>
            <Select value={newCircuitState} onChange={(e) => setNewCircuitState(e.target.value as string)} label="New Circuit State">
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="half_open">Half Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Next Test At" variant="outlined" type="datetime-local"
            value={nextTestAt} onChange={(e) => setNextTestAt(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ min: getMinDateTime() }}
            helperText="When the next health check test should happen (default: 10 mins from now)" />
          <TextField fullWidth margin="normal" label="Notes (Optional)" variant="outlined" multiline rows={3}
            value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Enter reason for circuit state change..." />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={handleUpdateCircuitState} color="primary" variant="contained" disabled={submitting || !newCircuitState}>
            {submitting ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BrandCatalogTilloHealth;
